// <s-personalise> — Eye Photo Necklace personalisation.
// - Opens a free-form crop editor (8 resize handles, zoom in/out, Cancel / Select) when a photo is chosen,
//   then puts the cropped JPG back into the file input and shows a preview with replace / re-crop actions.
const MIN_SIZE = 40;
const MAX_OUTPUT = 3000;
const ZOOM_STEP = 1.25;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

class SPersonalise extends HTMLElement {
  #ac;
  #previewUrl;
  #editorUrl;
  #originalFile = null;
  #committed = false;
  #box = { x: 0, y: 0, w: 0, h: 0 };
  #drag = null;
  #baseWidth = 0;
  #zoom = 1;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', (event) => this.#onChange(event), opts);
    this.addEventListener('click', (event) => this.#onClick(event), opts);
    this.addEventListener('pointerdown', (event) => this.#onPointerDown(event), opts);
    this.addEventListener('pointermove', (event) => this.#onPointerMove(event), opts);
    this.addEventListener('pointerup', () => { this.#drag = null; }, opts);
    this.addEventListener('pointercancel', () => { this.#drag = null; }, opts);
    this.#dialog?.addEventListener('cancel', (event) => { event.preventDefault(); this.#cancel(); }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
    if (this.#editorUrl) URL.revokeObjectURL(this.#editorUrl);
  }

  get #dialog() { return this.querySelector('[data-crop-dialog]'); }
  get #viewport() { return this.querySelector('[data-crop-viewport]'); }
  get #image() { return this.querySelector('[data-crop-image]'); }
  get #cropBox() { return this.querySelector('[data-crop-box]'); }
  get #fileInput() { return this.querySelector('input[type="file"]'); }

  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'file') return;

    const file = target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      this.#originalFile = null;
      this.#committed = false;
      this.#showPhoto(null);
      return;
    }

    this.#originalFile = file;
    this.#committed = false;
    this.#openEditor();
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-photo-replace]')) this.#fileInput?.click();
    if (target.closest('[data-crop-edit]') && this.#originalFile) this.#openEditor();
    if (target.closest('[data-crop-cancel]')) this.#cancel();
    if (target.closest('[data-crop-apply]')) this.#applyCrop();

    const zoom = target.closest('[data-crop-zoom]')?.getAttribute('data-crop-zoom');
    if (zoom === 'in') this.#setZoom(this.#zoom * ZOOM_STEP);
    if (zoom === 'out') this.#setZoom(this.#zoom / ZOOM_STEP);
  }

  // Crop editor
  #openEditor() {
    const dialog = this.#dialog;
    const image = this.#image;
    if (!(dialog instanceof HTMLDialogElement) || !(image instanceof HTMLImageElement) || !this.#originalFile) return;

    if (this.#editorUrl) URL.revokeObjectURL(this.#editorUrl);
    this.#editorUrl = URL.createObjectURL(this.#originalFile);

    image.onload = () => {
      image.width = image.naturalWidth;
      image.height = image.naturalHeight;
      requestAnimationFrame(() => this.#fitImage());
    };
    image.src = this.#editorUrl;

    if (!dialog.open) dialog.showModal();
  }

  // Cancel keeps the last selected photo; on a first upload it clears the selection.
  #cancel() {
    if (!this.#committed) {
      const input = this.#fileInput;
      if (input instanceof HTMLInputElement) input.value = '';
      this.#originalFile = null;
      this.#showPhoto(null);
    }
    this.#closeEditor();
  }

  #closeEditor() {
    const dialog = this.#dialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    this.#drag = null;
  }

  #fitImage() {
    const image = this.#image;
    const viewport = this.#viewport;
    if (!(image instanceof HTMLImageElement) || !(viewport instanceof HTMLElement) || !image.naturalWidth) return;

    const availW = viewport.clientWidth - 40; // viewport has 20px padding so edge handles stay grabbable
    const availH = Math.max(200, window.innerHeight * 0.72);
    const fit = Math.min(availW / image.naturalWidth, availH / image.naturalHeight);
    this.#baseWidth = image.naturalWidth * fit;
    this.#zoom = 1;
    image.style.width = `${this.#baseWidth}px`;

    const w = image.clientWidth;
    const h = image.clientHeight;
    this.#box = { x: 0, y: 0, w, h };
    this.#renderBox();
  }

  #setZoom(next) {
    const image = this.#image;
    const viewport = this.#viewport;
    if (!(image instanceof HTMLImageElement) || !(viewport instanceof HTMLElement) || !this.#baseWidth) return;

    const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    const ratio = zoom / this.#zoom;
    if (ratio === 1) return;

    this.#zoom = zoom;
    image.style.width = `${this.#baseWidth * zoom}px`;
    const { x, y, w, h } = this.#box;
    this.#box = { x: x * ratio, y: y * ratio, w: w * ratio, h: h * ratio };
    this.#renderBox();

    // Keep the crop box centred in view after zooming.
    viewport.scrollLeft = this.#box.x + this.#box.w / 2 - viewport.clientWidth / 2;
    viewport.scrollTop = this.#box.y + this.#box.h / 2 - viewport.clientHeight / 2;
  }

  #renderBox() {
    const el = this.#cropBox;
    if (!(el instanceof HTMLElement)) return;
    const { x, y, w, h } = this.#box;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.width = `${w}px`;
    el.style.height = `${h}px`;
  }

  #onPointerDown(event) {
    const target = event.target instanceof Element ? event.target : null;
    const box = target?.closest('[data-crop-box]');
    if (!box) return;

    event.preventDefault();
    const handle = target.closest('[data-handle]')?.getAttribute('data-handle') || 'move';
    this.#drag = { handle, startX: event.clientX, startY: event.clientY, start: { ...this.#box } };
    box.setPointerCapture?.(event.pointerId);
  }

  #onPointerMove(event) {
    if (!this.#drag) return;
    const image = this.#image;
    if (!image) return;

    const maxW = image.clientWidth;
    const maxH = image.clientHeight;
    const dx = event.clientX - this.#drag.startX;
    const dy = event.clientY - this.#drag.startY;
    const s = this.#drag.start;
    const handle = this.#drag.handle;
    const isRtl = getComputedStyle(this).direction === 'rtl';

    let { x, y, w, h } = s;

    if (handle === 'move') {
      x = Math.min(Math.max(0, s.x + dx), maxW - s.w);
      y = Math.min(Math.max(0, s.y + dy), maxH - s.h);
    } else {
      const physical = this.#physicalHandle(handle, isRtl);
      if (physical.includes('w')) {
        const nx = Math.min(Math.max(0, s.x + dx), s.x + s.w - MIN_SIZE);
        w = s.w + (s.x - nx);
        x = nx;
      }
      if (physical.includes('e')) w = Math.min(Math.max(MIN_SIZE, s.w + dx), maxW - s.x);
      if (physical.includes('n')) {
        const ny = Math.min(Math.max(0, s.y + dy), s.y + s.h - MIN_SIZE);
        h = s.h + (s.y - ny);
        y = ny;
      }
      if (physical.includes('s')) h = Math.min(Math.max(MIN_SIZE, s.h + dy), maxH - s.y);
    }

    this.#box = { x, y, w, h };
    this.#renderBox();
  }

  // Handles use logical (start/end) positions; map them to the physical side they sit on.
  #physicalHandle(handle, isRtl) {
    if (!isRtl) return handle;
    return handle.replace('w', '#').replace('e', 'w').replace('#', 'e');
  }

  async #applyCrop() {
    const image = this.#image;
    if (!(image instanceof HTMLImageElement) || !this.#originalFile || !image.clientWidth) return;

    const scale = image.naturalWidth / image.clientWidth;
    const sx = Math.round(this.#box.x * scale);
    const sy = Math.round(this.#box.y * scale);
    const sw = Math.max(1, Math.round(this.#box.w * scale));
    const sh = Math.max(1, Math.round(this.#box.h * scale));
    const outScale = Math.min(1, MAX_OUTPUT / Math.max(sw, sh));

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw * outScale);
    canvas.height = Math.round(sh * outScale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) return;

    const baseName = this.#originalFile.name.replace(/\.[^.]+$/, '') || 'photo';
    this.#useFile(new File([blob], `${baseName}-cropped.jpg`, { type: 'image/jpeg' }));
  }

  #useFile(file) {
    const input = this.#fileInput;
    if (file && input instanceof HTMLInputElement && typeof DataTransfer !== 'undefined') {
      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
      } catch {
        // Browsers without DataTransfer support keep the original photo.
      }
    }

    this.#committed = true;
    this.#showPhoto(input?.files?.[0] || file);
    this.#closeEditor();
  }

  #showPhoto(file) {
    const drop = this.querySelector('[data-photo-drop]');
    const result = this.querySelector('[data-photo-result]');
    const preview = this.querySelector('[data-photo-preview]');

    if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
    this.#previewUrl = null;

    if (drop instanceof HTMLElement) drop.hidden = Boolean(file);
    if (result instanceof HTMLElement) result.hidden = !file;

    if (file && preview instanceof HTMLImageElement) {
      this.#previewUrl = URL.createObjectURL(file);
      preview.src = this.#previewUrl;
    }
  }
}

if (!customElements.get('s-personalise')) customElements.define('s-personalise', SPersonalise);
